import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { RouterProps } from 'react-router';
import { filter } from 'ramda';

import useWorkflow from 'hooks/useWorkflow';
import { Step } from 'hooks/useWorkflow';

import { selectTenant } from 'state/tenant';
import {
  selectCreditApplication,
  mergeApplication,
} from 'state/creditApplication';
import {
  selectInitialFinanceOffers,
  InstallmentsInitialOffer,
  SplitPayInitialOffer,
} from 'state/initialFinanceOffers';
import {
  ExpandedPanel,
  selectExpandedPanel,
  setExpandedPanel,
} from 'state/unifiedWidgets';

import FmcForm from 'components/FmcForm';

import CheckoutOfferIntroView from './CheckoutOfferIntroView';

import { isDecimalMoney } from 'utils/validations';

import useNavigationHelpers from 'hooks/useNavigationHelpers';
import useTranslate from 'hooks/useTranslate/useTranslate';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import { selectAnalytics, setAnalyticsState } from 'state/analytics';

export interface FormValues {
  intendedPurchaseAmount: string | null;
}

type PropTypes = RouterProps;

const CheckoutOfferIntro: React.FC<PropTypes> = () => {
  const { sendEvents } = useAnalytics();

  const { pathAfter } = useWorkflow();
  const { push } = useNavigationHelpers();
  const dispatch = useDispatch();

  const t = useTranslate();

  const initialFinanceOffers = useSelector(selectInitialFinanceOffers);
  const expandedPanel = useSelector(selectExpandedPanel);
  const { bread } = useSelector(selectTenant);
  const { prequalRequestStarted } = useSelector(selectAnalytics);

  if (!bread) {
    // Should never happens
    throw new Error('Invalid tenant');
  }

  const installmentOffers = useMemo(
    () =>
      filter(
        initialFinanceOffer =>
          initialFinanceOffer.paymentProductType === 'INSTALLMENTS',
        initialFinanceOffers || [],
      ) as InstallmentsInitialOffer[],
    [initialFinanceOffers],
  );

  const splitPayOffers = useMemo(
    () =>
      filter(
        initialFinanceOffer =>
          initialFinanceOffer.paymentProductType === 'SPLITPAY',
        initialFinanceOffers || [],
      ) as SplitPayInitialOffer[],
    [initialFinanceOffers],
  );

  const onPanelChange = (
    _event: React.ChangeEvent<{}>,
    panelName: string,
    newExpanded: boolean,
  ) => {
    if (newExpanded) {
      dispatch(setExpandedPanel(panelName as ExpandedPanel));
    } else {
      dispatch(setExpandedPanel(undefined));
    }
  };

  const { intendedPurchaseAmount } = useSelector(selectCreditApplication);

  useEffect(() => {
    if (intendedPurchaseAmount === null) {
      dispatch(
        mergeApplication({
          intendedPurchaseAmount: bread.defaultCartAmount.toString(),
        }),
      );
    }
  }, [bread.defaultCartAmount, dispatch, intendedPurchaseAmount]);

  const schema = useMemo(() => {
    return Yup.object().shape({
      intendedPurchaseAmount: Yup.string()
        .nullable()
        .required(t('unified.offerIntro.intendedPurchaseAmountRequired'))
        .concat(isDecimalMoney),
    });
  }, [t]);

  const initialValues = useMemo(() => ({ intendedPurchaseAmount }), [
    intendedPurchaseAmount,
  ]);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (!prequalRequestStarted) {
        sendEvents([Event.PrequalRequestStarted]);
        dispatch(setAnalyticsState({ prequalRequestStarted: true }));
      }
      dispatch(mergeApplication(schema.cast(values)));
      push(pathAfter(Step.UNIFIED_OFFER_INTRO));
    },
    [dispatch, pathAfter, push, sendEvents, schema, prequalRequestStarted],
  );

  if (!bread) {
    // Should never happens
    throw new Error('Invalid tenant');
  }

  return (
    <FmcForm
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {formProps => {
        const { intendedPurchaseAmount } = schema.cast(formProps.values);
        return (
          <CheckoutOfferIntroView
            intendedPurchaseAmount={intendedPurchaseAmount}
            formikProps={formProps}
            onPanelChange={onPanelChange}
            expandedPanel={expandedPanel}
            installmentOffers={installmentOffers}
            splitPayOffers={splitPayOffers}
            defaultCartAmount={bread.defaultCartAmount}
          />
        );
      }}
    </FmcForm>
  );
};

export default CheckoutOfferIntro;
